import { useState, useEffect, useMemo } from 'react';
import { Col, Row, Input, Label, Spinner } from 'reactstrap';
import { Search, ChevronDown, ChevronUp, Filter, CheckSquare, Square, Download } from 'react-feather';
import OptionsForm from '../../layouts/forms/OptionsForm';
import Loader from '../../layouts/loader/Loader';
import ModalClickable from '../../layouts/modals/ModalClickable';
import intercambioApiService from '../../services/http/intercambioApiService';
import otrosApiService from '../../services/http/otrosApiService';
import Table from '../ui/Table';
import colors from '../../theme/colors';

const INITIAL_OPTIONS = { centre: [], info: [], curs: [], grau: [] };

const FormularioIntercambio = () => {
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // Opciones
  const [options, setOptions] = useState(INITIAL_OPTIONS);

  // Datos del formulario.
  const [headquarter, setHeadquarter] = useState(null);
  const [courseYear, setCourseYear] = useState(null);
  const [type, setType] = useState(null);
  const [course, setCourse] = useState(null);
  const [isLOE2425, setIsLOE2425] = useState(false);

  // Datos de los alumnos.
  const [students, setStudents] = useState([]);
  const [studentsSelected, setStudentsSelected] = useState([]);
  const [allChecked, setAllChecked] = useState(false);

  // Filtro de documento de identidad.
  const [search, setSearch] = useState('');

  // Loaders.
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingAlumnos, setIsLoadingAlumnos] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      setIsLoadingOptions(true);

      try {
        const optionsResp = await otrosApiService.cargarOpcionesSII();

        setOptions((prev) => ({ ...prev, ...optionsResp }));
        setHeadquarter(optionsResp.centre[0].codigo);
        setCourseYear(optionsResp.curs[0].codigo);
        setCourse(optionsResp.grau[0].codigo);
        setType(optionsResp.info[0].codigo);
      } catch (error) {
        setOptions(INITIAL_OPTIONS);
        setHeadquarter(null);
        setCourseYear(null);
        setCourse(null);
        setType(null);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    fetchOptions();
  }, []);

  const handleSelection = (ccodcli) => {
    setStudentsSelected((prev) => {
      const newStudentsSelected = prev.includes(ccodcli)
        ? prev.filter((id) => id !== ccodcli)
        : [...prev, ccodcli];

      return newStudentsSelected;
    });
  };

  // Alumnos con la acción de seleccionar.
  const studentsWithAction = useMemo(
    () =>
      students.map((student) => ({
        ...student,
        actions: (
          <Input
            type="checkbox"
            id={student.ccodcli}
            name="option"
            checked={studentsSelected.includes(student.ccodcli)}
            onChange={() => handleSelection(student.ccodcli)}
          />
        ),
      })),
    [students, studentsSelected],
  );

  const handleSearchStudents = async (e) => {
    setIsLoadingAlumnos(true);
    if (e) e.preventDefault();

    try {
      const dataAlumnosActas = await intercambioApiService.buscarAlumnosServeiIntercanvi({
        headquarter,
        ano: courseYear,
        grado: course,
      });

      setStudents(dataAlumnosActas);
    } catch (error) {
      setStudents([]);
    } finally {
      setStudentsSelected([]);
      setAllChecked(false);
      setSearch('');
      setIsLoadingAlumnos(false);
    }
  };

  const handleCheckAll = () => {
    if (!allChecked) {
      const newStudentsSelected = students.map((a) => a.ccodcli);
      setStudentsSelected(newStudentsSelected);
    } else {
      setStudentsSelected([]);
    }

    setAllChecked((prev) => !prev);
  };

  const sendData = async () => {
    setIsDownloaded(true);

    try {
      const payload = {
        headquarter,
        evaluaciones: type,
        ano: courseYear,
        grado: course,
        isLOE2425,
        ccodclis: studentsSelected,
      };

      if (isLOE2425) {
        await intercambioApiService.exportToXML(payload);
      } else {
        await intercambioApiService.generarXML(payload);
      }
    } catch (error) {
      // No hacer nada específico aquí.
    }
  };

  const toolbarButtons = (
    <>
      <button
        type="button"
        onClick={handleSearchStudents}
        disabled={isLoadingAlumnos}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px', border: 'none',
          backgroundColor: colors.primary, color: '#fff',
          cursor: isLoadingAlumnos ? 'not-allowed' : 'pointer',
          opacity: isLoadingAlumnos ? 0.5 : 1, transition: 'all 0.15s',
        }}
      >
        {isLoadingAlumnos
          ? <Spinner style={{ height: '13px', width: '13px' }} />
          : <Filter size={13} />}
        Filtrar
      </button>
      <button
        type="button"
        onClick={handleCheckAll}
        disabled={!students.length}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
          border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#6b7280',
          cursor: !students.length ? 'not-allowed' : 'pointer',
          opacity: !students.length ? 0.5 : 1, transition: 'all 0.15s',
        }}
      >
        {allChecked ? <Square size={13} /> : <CheckSquare size={13} />}
        {allChecked ? 'Desmarcar' : 'Marcar todos'}
      </button>
      <button
        type="button"
        onClick={() => sendData()}
        disabled={!students.length || !studentsSelected.length}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
          fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
          border: `1px solid ${colors.primary}`, backgroundColor: '#fff',
          color: colors.primary,
          cursor: (!students.length || !studentsSelected.length) ? 'not-allowed' : 'pointer',
          opacity: (!students.length || !studentsSelected.length) ? 0.5 : 1,
          transition: 'all 0.15s',
        }}
      >
        <Download size={13} />
        Generar XML
      </button>
    </>
  );

  return (
    <>
      {isDownloaded ? (
        <ModalClickable
          color="primary"
          header="Petición recibida correctamente. El archivo se enviará a su correo electrónico una vez esté generado"
        />
      ) : (
        ''
      )}

      <div style={{
        backgroundColor: '#fff', borderRadius: '12px',
        border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)',
        overflow: 'visible',
      }}>
        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: showFilters ? '1px solid #f3f4f6' : 'none',
        }}>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 600, color: '#374151',
              padding: '4px 8px', borderRadius: '6px',
            }}
          >
            <Search size={15} style={{ color: '#9ca3af' }} />
            Filtros
            {showFilters
              ? <ChevronUp size={14} style={{ color: '#9ca3af' }} />
              : <ChevronDown size={14} style={{ color: '#9ca3af' }} />}
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {toolbarButtons}
          </div>
        </div>

        {/* Filtros colapsables */}
        {showFilters && (
          <div style={{
            padding: '16px',
            opacity: isLoadingAlumnos ? 0.5 : 1,
            pointerEvents: isLoadingAlumnos ? 'none' : 'auto',
            transition: 'opacity 0.2s',
          }}>
            {!isLoadingOptions ? (
              <>
                <Row className="mb-3">
                  <Col sm>
                    <Label for="centros">Centro</Label>
                    <Input
                      type="select"
                      name="centros"
                      id="centros"
                      onChange={({ target: { value } }) => setHeadquarter(value)}
                    >
                      <OptionsForm datos={options.centre} />
                    </Input>
                  </Col>
                  <Col sm className="mt-2 mt-md-0">
                    <Label for="tipo">Tipo</Label>
                    <Input
                      type="select"
                      name="tipo"
                      id="tipo"
                      onChange={({ target: { value } }) => setType(value)}
                    >
                      <OptionsForm datos={options.info} />
                    </Input>
                  </Col>
                </Row>
                <Row className="mt-0 mt-md-2">
                  <Col sm className="mt-2 mt-md-0">
                    <Label for="curso">Curso</Label>
                    <Input
                      type="select"
                      name="curso"
                      id="curso"
                      onChange={({ target: { value } }) => setCourseYear(value)}
                    >
                      <OptionsForm datos={options.curs} />
                    </Input>
                  </Col>
                  <Col sm className="mt-2 mt-md-0">
                    <Label for="grado">Grado</Label>
                    <Input
                      type="select"
                      name="grado"
                      id="grado"
                      onChange={({ target: { value } }) => setCourse(value)}
                    >
                      <OptionsForm datos={options.grau} />
                    </Input>
                  </Col>
                </Row>
                <Row className="mt-4 mt-md-4">
                  <Col sm className="mt-2 mt-md-0">
                    <Label check>
                      LOE2425{' '}
                      <Input
                        type="checkbox"
                        name="isLOE2425"
                        id="isLOE2425"
                        checked={isLOE2425}
                        onChange={({ target: { checked } }) => setIsLOE2425(checked)}
                      />
                    </Label>
                  </Col>
                </Row>
              </>
            ) : (
              <Loader />
            )}
          </div>
        )}
      </div>

      {students.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <Row>
            <Col sm md={6}>
              <Label for="search">Buscar</Label>
              <Input
                name="search"
                id="search"
                value={search}
                onChange={({ target: { value } }) => setSearch(value)}
              />
            </Col>
          </Row>
          <Table
            className="mt-2"
            columns={[
              { Header: 'Seleccionar', accessor: 'actions', sortable: false },
              { Header: 'ID', accessor: 'ccodcli' },
              { Header: 'Nombre', accessor: 'sNombre' },
              { Header: 'Apellidos', accessor: 'sApellidos' },
              { Header: 'Documento', accessor: 'CDNICIF' },
            ]}
            data={studentsWithAction.filter((student) => {
              const fields = ['ccodcli', 'sNombre', 'sApellidos', 'CDNICIF'];
              return fields.some((field) =>
                student[field]?.toString().toLowerCase().includes(search.toLowerCase()),
              );
            })}
          />
        </div>
      )}
    </>
  );
};

export default FormularioIntercambio;
